import { z } from 'zod';
import { IllegalArgumentError } from 'faro-common-model';
import { nullishToUndefined } from '../utils/nullishToUndefined.js';
import { nullishToEmptyArray } from '../utils/nullishToEmptyArray.js';
import { uuidish } from '../utils/uuidish.js';
export var PropertyType;
(function (PropertyType) {
    PropertyType["System"] = "SYSTEM";
    PropertyType["Scale"] = "SCALE";
    PropertyType["Property"] = "PROPERTY";
    PropertyType["Time"] = "TIME";
})(PropertyType || (PropertyType = {}));
const bclNamedObjectInternalSchema = z.looseObject({
    id: z.string(),
    name: z.string(),
    code: z.string(),
});
export const diseaseAreaSchema = bclNamedObjectInternalSchema;
export const locationSchema = bclNamedObjectInternalSchema;
export const studyContributorSchema = bclNamedObjectInternalSchema;
export const therapeuticAreaSchema = bclNamedObjectInternalSchema;
export const bclOtherNamesSchema = z.looseObject({
    longCommonName: z.string(),
    abbreviatedName: z.string(),
    baseName: z.string().nullish().transform(nullishToUndefined).optional(),
});
export const bclExternalCodesSchema = z.looseObject({
    loinc: z.string().nullish().transform(nullishToUndefined).optional(),
});
export const bclPropertyCodesSchema = z.looseObject({
    loinc: z.string().nullish().transform(nullishToUndefined).optional(),
});
export const bclPropertyOtherNamesSchema = z.looseObject({
    abbreviatedName: z.string(),
});
const bclPropertyInternalSchema = z.looseObject({
    name: z.string(),
    codes: bclPropertyCodesSchema,
    otherNames: bclPropertyOtherNamesSchema,
    // Add string as an option to account for BCL expansion
    propertyType: z.enum(PropertyType).or(z.string()),
});
export const bclPropertySchema = bclPropertyInternalSchema;
const bclAssessmentMeasurementCostInternalSchema = z.looseObject({
    cost: z.unknown(),
    studyContributor: studyContributorSchema.nullish().transform(nullishToUndefined).optional(),
    locationCode: z.string().nullish().transform(nullishToUndefined).optional(),
});
export const bclAssessmentMeasurementCostSchema = bclAssessmentMeasurementCostInternalSchema;
const bclMonetaryCostInternalSchema = z.looseObject({
    ...bclAssessmentMeasurementCostInternalSchema.shape,
    cost: z.looseObject({
        minCost: z.number().nullish().transform(nullishToUndefined).optional(),
        maxCost: z.number().nullish().transform(nullishToUndefined).optional(),
        medianCost: z.number().nullish().transform(nullishToUndefined).optional(),
        averageCost: z.number().nullish().transform(nullishToUndefined).optional(),
    }),
});
export const bclMonetaryCostSchema = bclMonetaryCostInternalSchema;
const bclRelativeValueUnitCostInternalSchema = z.looseObject({
    ...bclAssessmentMeasurementCostInternalSchema.shape,
    cost: z.looseObject({
        cost: z.number(),
        malpracticeCost: z.number(),
    }),
});
export const bclRelativeValueUnitCostSchema = bclRelativeValueUnitCostInternalSchema;
const bclTimeCostInternalSchema = z.looseObject({
    ...bclAssessmentMeasurementCostInternalSchema.shape,
    cost: z.number(),
});
export const bclTimeCostSchema = bclTimeCostInternalSchema;
const bclBloodVolumeCostInternalSchema = z.looseObject({
    ...bclAssessmentMeasurementCostInternalSchema.shape,
    cost: z.number(),
});
export const bclBloodVolumeCostSchema = bclBloodVolumeCostInternalSchema;
const bclAssessmentMeasurementCostsInternalSchema = z.looseObject({
    timeCosts: z.array(bclTimeCostSchema).nullish().transform(nullishToUndefined).optional(),
    monetaryCosts: z.array(bclMonetaryCostSchema).nullish().transform(nullishToUndefined).optional(),
    relativeValueUnitCosts: z.array(bclRelativeValueUnitCostSchema).nullish().transform(nullishToUndefined).optional(),
    bloodVolumeCost: bclBloodVolumeCostInternalSchema.nullish().transform(nullishToUndefined).optional(),
});
export const bclAssessmentMeasurementCostsSchema = bclAssessmentMeasurementCostsInternalSchema;
const bclBaseAssessmentMeasurementSchema = z.looseObject({
    id: uuidish,
    name: z.string(),
    class: z.string(),
    classType: z.string(),
    version: z.number().int(),
    otherNames: bclOtherNamesSchema,
    externalCodes: bclExternalCodesSchema,
    costs: bclAssessmentMeasurementCostsInternalSchema.nullish().transform(nullishToUndefined).optional(),
    time: bclPropertySchema,
    scale: bclPropertySchema,
    property: bclPropertySchema,
    system: bclPropertySchema,
    conditionality: z.string().nullish().transform(nullishToUndefined).optional(),
    description: z.string().nullish().transform(nullishToUndefined).optional(),
    locationCodes: z.array(z.string()).nullish().transform(nullishToEmptyArray),
    units: z.array(z.string()).nullish().transform(nullishToEmptyArray),
    sampleType: z.string().nullish().transform(nullishToUndefined).optional(),
    tenantId: z.string().nullish().transform(nullishToUndefined).optional(),
});
/**
 * DEV NOTE: This schema type dancing is required to account for Zod's inability to infer the
 * type definition of a lazy/recursive property.
 *
 * @see AssessmentMeasurement
 * @see bclBaseAssessmentMeasurementSchema
 * @see bclAssessmentMeasurementSchema
 */
export const bclAssessmentMeasurementSchema = z.looseObject({
    ...bclBaseAssessmentMeasurementSchema.shape,
    children: z.lazy(() => z.array(bclAssessmentMeasurementSchema).nullish().transform(nullishToUndefined).optional()),
});
export function toAssessmentMeasurement(assessmentMeasurement) {
    const result = bclAssessmentMeasurementSchema.safeParse(assessmentMeasurement);
    if (!result.success) {
        throw new IllegalArgumentError(`Invalid AssessmentMeasurement object. ${result.error}`);
    }
    return result.data;
}
const bclAssessmentMeasurementChildrenAwareInternalSchema = z.looseObject({
    ...bclBaseAssessmentMeasurementSchema.shape,
    hasChildren: z.coerce.boolean(),
});
export const bclAssessmentMeasurementChildrenAwareSchema = bclAssessmentMeasurementChildrenAwareInternalSchema;
export function toAssessmentMeasurementChildrenAware(assessmentMeasurementChildrenAware) {
    const result = bclAssessmentMeasurementChildrenAwareInternalSchema.safeParse(assessmentMeasurementChildrenAware);
    if (!result.success) {
        throw new IllegalArgumentError(`Invalid assessmentMeasurementChildrenAware object. ${result.error.message}`);
    }
    return result.data;
}
const bclAssessmentMeasurementMetadataInternalSchema = z.looseObject({
    id: uuidish,
    assessmentMeasurementId: uuidish,
    isMostCommon: z.coerce.boolean().default(false),
    isDeprecated: z.coerce.boolean().default(false),
    therapeuticAreas: z.array(z.string()).nullish().transform(nullishToEmptyArray),
    diseaseAreas: z.array(z.string()).nullish().transform(nullishToEmptyArray),
});
export const bclAssessmentMeasurementMetadataSchema = bclAssessmentMeasurementMetadataInternalSchema;
export function toAssessmentMeasurementMetadata(metadata) {
    const result = bclAssessmentMeasurementMetadataInternalSchema.safeParse(metadata);
    if (!result.success) {
        throw new IllegalArgumentError(`Invalid AssessmentMeasurementMetadata object. ${result.error.message}`);
    }
    return result.data;
}
const bclClassTypeInternalSchema = z.looseObject({
    ...bclNamedObjectInternalSchema.shape,
    description: z.string(),
});
export const bclClassTypeSchema = bclClassTypeInternalSchema;
export function toClassType(classType) {
    const result = bclClassTypeSchema.safeParse(classType);
    if (!result.success) {
        throw new IllegalArgumentError(`Invalid ClassType object. ${result.error}`);
    }
    return result.data;
}
export function toDiseaseArea(diseaseArea) {
    const result = diseaseAreaSchema.safeParse(diseaseArea);
    if (!result.success) {
        throw new IllegalArgumentError(`Invalid DiseaseArea object. ${result.error.message}`);
    }
    return result.data;
}
export function toLocation(location) {
    const result = locationSchema.safeParse(location);
    if (!result.success) {
        throw new IllegalArgumentError(`Invalid Location object. ${result.error.message}`);
    }
    return result.data;
}
export function toTherapeuticArea(therapeuticArea) {
    const result = therapeuticAreaSchema.safeParse(therapeuticArea);
    if (!result.success) {
        throw new IllegalArgumentError(`Invalid TherapeuticArea object. ${result.error.message}`);
    }
    return result.data;
}
export function toStudyContributor(studyContributor) {
    const result = studyContributorSchema.safeParse(studyContributor);
    if (!result.success) {
        throw new IllegalArgumentError(`Invalid StudyContributor object. ${result.error.message}`);
    }
    return result.data;
}
//# sourceMappingURL=biomedical-library-proxy.js.map